import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Search, ImageOff, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/services/api';

interface ImageResult {
  full: string;
  thumb: string;
  title?: string;
  source?: string;
}

interface Props {
  initialQuery: string;
  onSelect: (url: string) => void;
  onClose: () => void;
}

export function ImageSearchModal({ initialQuery, onSelect, onClose }: Props) {
  const [query, setQuery] = useState(initialQuery);
  // Termo efetivamente buscado. Inicia com initialQuery para auto-buscar ao abrir.
  const [submittedQuery, setSubmittedQuery] = useState(initialQuery.trim());
  const [brokenSet, setBrokenSet] = useState<Set<string>>(new Set());
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // useQuery lida com StrictMode nativamente (dedupe + cache) — sem efeito manual
  // nem ref de guarda, o que evita a busca automática se perder no remount do dev.
  const searchQuery = useQuery({
    queryKey: ['product-image-search', submittedQuery],
    queryFn: async () => {
      const { data } = await apiClient.searchProductImages(submittedQuery, 12);
      return (data?.items ?? []) as ImageResult[];
    },
    enabled: submittedQuery.length > 0,
    staleTime: 5 * 60 * 1000, // reaproveita a mesma busca por 5min
    retry: false,
  });

  const results = searchQuery.data ?? [];
  const loading = searchQuery.isFetching;

  // Reseta imagens quebradas a cada nova busca
  useEffect(() => { setBrokenSet(new Set()); }, [submittedQuery]);

  // Foca o input quando abre sem query inicial
  useEffect(() => {
    if (!initialQuery.trim()) inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Erro / vazio
  useEffect(() => {
    if (searchQuery.isError) toast.error('Erro ao buscar imagens');
  }, [searchQuery.isError]);

  // ESC fecha
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    if (q === submittedQuery) searchQuery.refetch(); // mesma busca: força refresh
    else setSubmittedQuery(q);
  }

  const visibleResults = results.filter(r => !brokenSet.has(r.full));

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <div className="grid place-items-center h-9 w-9 rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow">
            <Sparkles className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-bold leading-tight">Buscar imagem do produto</h2>
            <p className="text-xs text-muted-foreground">Selecione uma imagem dos resultados abaixo</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1.5 rounded hover:bg-muted transition"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search bar */}
        <form onSubmit={onSubmit} className="px-6 py-4 border-b bg-muted/30 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Ex: Coca Cola 2L"
              className="pl-9"
              autoFocus
            />
          </div>
          <Button type="submit" variant="gradient" loading={loading} disabled={!query.trim()}>
            <Search className="h-4 w-4" />
            Buscar
          </Button>
        </form>

        {/* Results grid */}
        <div className="flex-1 overflow-y-auto p-6 min-h-[300px]">
          {loading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-lg bg-muted animate-pulse"
                  style={{ animationDelay: `${i * 60}ms` }}
                />
              ))}
            </div>
          )}

          {!loading && results.length === 0 && (
            <div className="h-full grid place-items-center text-center text-muted-foreground py-12">
              <div>
                <ImageOff className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">
                  {searchQuery.isError
                    ? 'Não foi possível buscar imagens. Tente outra busca.'
                    : 'Digite o nome do produto e clique em buscar'}
                </p>
              </div>
            </div>
          )}

          {!loading && visibleResults.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {visibleResults.map((r, idx) => (
                <button
                  key={r.full}
                  type="button"
                  onClick={() => onSelect(r.full)}
                  onMouseEnter={() => setHoverIdx(idx)}
                  onMouseLeave={() => setHoverIdx(null)}
                  className={[
                    'group relative aspect-square rounded-lg overflow-hidden border bg-muted',
                    'transition-all duration-200',
                    'hover:scale-[1.03] hover:shadow-lg hover:border-violet-500',
                    'focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-background',
                  ].join(' ')}
                  title={r.title || ''}
                >
                  <img
                    src={r.thumb}
                    alt={r.title || ''}
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    onError={() => setBrokenSet(prev => {
                      const next = new Set(prev);
                      next.add(r.full);
                      return next;
                    })}
                  />
                  {/* overlay no hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute inset-x-0 bottom-0 p-2 translate-y-1 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                    <div className="text-[11px] text-white font-medium drop-shadow line-clamp-2">
                      {r.title || 'Selecionar'}
                    </div>
                  </div>
                  {hoverIdx === idx && (
                    <div className="absolute top-2 right-2 grid place-items-center h-7 w-7 rounded-full bg-violet-600 text-white shadow-lg">
                      <Sparkles className="h-3.5 w-3.5" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {!loading && results.length > 0 && visibleResults.length === 0 && (
            <div className="h-full grid place-items-center text-center text-muted-foreground py-12">
              <div>
                <ImageOff className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm">Todas as imagens falharam ao carregar. Tente outra busca.</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-muted/30 flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            {loading ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> Buscando…</>
            ) : (
              <>Clique numa imagem para selecionar</>
            )}
          </span>
          <span>{visibleResults.length} resultado{visibleResults.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
}
